import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import {
	formatStatusTag,
	getActionPlanStatusColor,
} from '../../../../utils/actionPlanStatusBadge';

// Import logo (bundled asset)
const warehouseNavigatorLogo = require('../../../../assets/warehouse_navigator.png');

type PrintOrientation = 'landscape' | 'portrait';

// This matches the "transformedData" structure passed to `DataTable`.
export type ActionPlanPrintRow = {
	id: string;
	title: string;
	assignedUser: string;
	status: string;
	targetDate: string;
	source?: string;
	badgeText?: string;
	rawData?: {
		ASSIGNEE_EMAIL?: string;
		OWNER_EMAIL?: string;
		LOCATION?: string;
		OWNER_ROLE?: string;
		ASSIGNMENT_STATUS?: string;
	};
	expansionData?: {
		actionPlanOwner: string;
		type: string;
		startDate: string;
		source: string;
		frontlineEmployee: string;
		actionPlan: string;
	};
};

export interface ActionPlanListPrintRef {
	print: () => void;
	getHtml?: () => string;
}

type ActionPlanListPrintProps = {
	transformedData: ActionPlanPrintRow[];
	orientation?: PrintOrientation;
	/**
	 * Optional hook for native/mobile printing.
	 * Example usage: `onMobilePrint={(html) => RNPrint.print({ html })}`
	 */
	onMobilePrint?: (html: string) => void | Promise<void>;
};

// -----------------------------
// Pure utilities (no side effects)
// -----------------------------
const escapeHtml = (text: unknown): string => {
	const div = typeof document !== 'undefined' ? document.createElement('div') : null;
	if (div) {
		div.textContent = String(text ?? '');
		return div.innerHTML;
	}

	return String(text ?? '').replace(/[&<>"']/g, (m) =>
		(
			{
				'&': '&amp;',
				'<': '&lt;',
				'>': '&gt;',
				'"': '&quot;',
				"'": '&#39;',
			} as const
		)[m] || m,
	);
};

const formatPrintDate = (d: Date): string =>
	d.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});

// -----------------------------
// HTML rendering helpers
// -----------------------------
export const renderPrintHeader = (args: { date: string; logoBase64?: string }) => {
	const logoHtml = args.logoBase64
		? `<img class="logo" src="${args.logoBase64}" alt="Warehouse Navigator" />`
		: `<div class="logo" style="background: transparent;"></div>`;

	return `
		<tr>
			<th class="print-header-cell" colspan="5">
				<div class="header-bar">
					<div class="header-left">
						${logoHtml}
						<span class="header-title">Warehouse Navigator</span>
					</div>
					<span class="header-date">${escapeHtml(args.date)}</span>
				</div>
			</th>
		</tr>
	`;
};

export const renderSectionHeader = () => {
	return `
		<tr>
			<th class="section-title-cell" colspan="5">
				<div class="section-row">
					<span class="section-title">Action Plans</span>
				</div>
			</th>
		</tr>
	`;
};

export const renderTableHeader = () => {
	return `
		<tr class="table-header-row">
			<th class="th title">TITLE</th>
			<th class="th assigned">ASSIGNED USER</th>
			<th class="th status">STATUS</th>
			<th class="th target">TARGET DATE</th>
			<th class="th source">SOURCE</th>
		</tr>
	`;
};

export const renderExpandedSection = (item: ActionPlanPrintRow) => {
	const exp = item.expansionData;
	if (!exp) return '';

	return `
		<div class="expanded">
			<div class="expanded-top">
				<div class="kv">
					<div class="k">Action Plan Owner</div>
					<div class="v">${escapeHtml(exp.actionPlanOwner || '')}</div>
				</div>
				<div class="kv">
					<div class="k">Type</div>
					<div class="v">${escapeHtml(exp.type || '')}</div>
				</div>
				<div class="kv">
					<div class="k">Start Date</div>
					<div class="v">${escapeHtml(exp.startDate || '')}</div>
				</div>
				<div class="kv">
					<div class="k">Source</div>
					<div class="v">${escapeHtml(exp.source || '')}</div>
				</div>
				<div class="kv">
					<div class="k">Frontline Employee</div>
					<div class="v">${escapeHtml(exp.frontlineEmployee || '')}</div>
				</div>
			</div>

			<div class="expanded-bottom">
				<div class="kv wide">
					<div class="k">Action Plan</div>
					<div class="v2">${escapeHtml(exp.actionPlan || '')}</div>
				</div>
			</div>
		</div>
	`;
};

export const renderActionPlanRow = (item: ActionPlanPrintRow) => {
	const statusText = formatStatusTag(item.status || '');
	const statusColor = getActionPlanStatusColor(item.status || '');
	const sourceText = item.source ?? item.expansionData?.source ?? '';

	return `
		<tr class="record-row">
			<td class="record-cell" colspan="5">
				<div class="record-card">
					<div class="primary-grid">
						<div class="cell title">
							<div class="title-text">${escapeHtml(item.title || '')}</div>
						</div>
						<div class="cell assigned">
							<div class="value">${escapeHtml(item.assignedUser || '')}</div>
						</div>
						<div class="cell status">
							<span class="status-badge" style="background-color: ${statusColor};">
								${escapeHtml(statusText)}
							</span>
						</div>
						<div class="cell target">
							<div class="value">${escapeHtml(item.targetDate || '')}</div>
						</div>
						<div class="cell source">
							<div class="value">${escapeHtml(sourceText)} </div>
						</div>
					</div>

					${renderExpandedSection(item)}
				</div>
			</td>
		</tr>
	`;
};

export const renderStyles = (orientation: PrintOrientation) => {
	const pageRule =
		orientation === 'portrait'
			? `@page { size: portrait; margin: 0.5in 0.5in; }`
			: `@page { size: landscape; margin: 0.5in 0.25in; }`;

	const cols =
		orientation === 'portrait'
			? {
				title: '34%',
				assigned: '18%',
				status: '14%',
				target: '17%',
				source: '17%',
			}
			: {
				title: '36%',
				assigned: '18%',
				status: '14%',
				target: '16%',
				source: '16%',
			};

	return `
		${pageRule}


		html, body { padding: 0; margin: 0; }
		body {
			font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
			color: #1C1C1C;
			background: #FFFFFF;
		}

		* { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }

		table.report-table { width: 100%; border-collapse: collapse; }

		thead { display: table-header-group; }
		tbody { display: table-row-group; }

		tr { page-break-inside: avoid; break-inside: avoid; }

		.print-header-cell {
			padding: 0 0 10px 0;
			border-bottom: 0;
			text-align: left;
			font-weight: normal;
		}

		.header-bar {
			display: flex;
			align-items: center;
			justify-content: space-between;
			border: 1px solid #DADADA;
			border-radius: 6px;
			padding: 8px 12px;
			background: #FFFFFF;
		}

		.header-left { display: flex; align-items: center; gap: 8px; min-width: 0; }
		.logo { width: 20px; height: 20px; object-fit: contain; border-radius: 4px; flex-shrink: 0; }
		.header-title { font-weight: 700; font-size: 16px; color: #093F6E; white-space: nowrap; }
		.header-date { font-size: 13px; font-weight: 400; color: #6B6B6B; white-space: nowrap; }

		.section-title-cell {
			padding: 0 0 10px 0;
			border: 0;
			text-align: left;
			font-weight: normal;
		}

		.section-row {
			display: flex;
			align-items: center;
			justify-content: flex-start;
			padding: 10px 0 12px;
		}
		.section-title { font-weight: 700; font-size: 14px; letter-spacing: 0.2px; }

		.table-header-row th {
			text-align: left;
			font-size: 10px;
			letter-spacing: 0.02em;
			color: #5A5A5A;
			padding: 8px 10px;
			border-top: 1px solid #D7E5F8;
			border-bottom: 1px solid #D7E5F8;
			background: #FFFFFF;
		}

		th.title { width: ${cols.title}; }
		th.assigned { width: ${cols.assigned}; }
		th.status { width: ${cols.status}; }
		th.target { width: ${cols.target}; }
		th.source { width: ${cols.source}; }

		.record-cell {
			padding: 0;
			border-bottom: 1px solid #E6E6E6;
		}

		.record-card {
			padding: 10px 10px 12px 10px;
			break-inside: avoid;
			page-break-inside: avoid;
		}

		.primary-grid {
			display: grid;
			grid-template-columns: ${cols.title} ${cols.assigned} ${cols.status} ${cols.target} ${cols.source};
			gap: 0;
			align-items: start;
		}

		.cell {
			padding: 0 10px 0 0;
		}

		.title-text {
			font-size: 12px;
			font-weight: 700;
			line-height: 1.2;
		}

		.value {
			font-size: 12px;
			font-weight: 600;
			line-height: 1.2;
		}

		.status-badge {
			display: inline-block;
			padding: 3px 8px;
			border-radius: 6px;
			font-size: 11px;
			font-weight: 700;
			color: #FFFFFF;
			white-space: nowrap;
		}

		.expanded {
			margin-top: 10px;
		}

		.expanded-top {
			display: grid;
			grid-template-columns: repeat(5, 1fr);
			gap: 10px 18px;
		}

		.k {
			font-size: 9px;
			color: #7A7A7A;
			margin-bottom: 4px;
		}

		.v {
			font-size: 11px;
			color: #2D2D2D;
			font-weight: 500;
		}

		.expanded-bottom {
			margin-top: 10px;
		}

		.kv.wide { max-width: 100%; }

		.v2 {
			font-size: 11px;
			color: #2D2D2D;
			font-weight: 500;
			line-height: 1.3;
		}

		@media print { * { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
	`;
};

export const generateActionPlanPrintHTML = (args: {
	date: string;
	logoBase64?: string;
	transformedData: ActionPlanPrintRow[];
	orientation: PrintOrientation;
}): string => {
	const headerHtml = `
		<thead>
			${renderPrintHeader({ date: args.date, logoBase64: args.logoBase64 })}
			${renderSectionHeader()}
			${renderTableHeader()}
		</thead>
	`;

	const rowsHtml = (args.transformedData || []).map(renderActionPlanRow).join('');

	return `<!DOCTYPE html>
<html lang="en">
	<head>
		<meta charset="UTF-8" />
		<meta name="viewport" content="width=device-width, initial-scale=1.0" />
		<title>Action Plans Print</title>
		<style>
			${renderStyles(args.orientation)}
		</style>
	</head>
	<body>
		<table class="report-table">
			${headerHtml}
			<tbody>
				${rowsHtml}
			</tbody>
		</table>
	</body>
</html>`;
};

const ActionPlanListPrint = forwardRef(function ActionPlanListPrint(
	props: ActionPlanListPrintProps,
	ref: React.Ref<ActionPlanListPrintRef>,
) {
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	const [logoBase64, setLogoBase64] = useState<string>('');

	useEffect(() => {
		const loadLogo = async () => {
			if (Platform.OS !== 'web') return;
			try {
				const response = await fetch(warehouseNavigatorLogo);
				const blob = await response.blob();
				const reader = new FileReader();
				reader.onloadend = () => setLogoBase64(reader.result as string);
				reader.readAsDataURL(blob);
			} catch (e) {
				console.warn('Failed to load logo for print:', e);
				setLogoBase64('');
			}
		};
		loadLogo();
	}, []);

	const printDate = useMemo(() => formatPrintDate(new Date()), []);
	const orientation: PrintOrientation = props.orientation || 'landscape';

	const htmlContent = useMemo(
		() =>
			generateActionPlanPrintHTML({
				date: printDate,
				logoBase64,
				transformedData: props.transformedData || [],
				orientation,
			}),
		[printDate, logoBase64, props.transformedData, orientation],
	);

	useImperativeHandle(
		ref,
		() => ({
			print: () => {
				if (Platform.OS === 'web') {
					const iframe = iframeRef.current;
					if (iframe?.contentWindow) {
						iframe.contentWindow.focus();
						iframe.contentWindow.print();
					}
					return;
				}

				// Mobile: let the caller decide how to print (react-native-print or HTML->PDF).
				props.onMobilePrint?.(htmlContent);
			},
			getHtml: () => htmlContent,
		}),
		[htmlContent, props],
	);

	useEffect(() => {
		if (Platform.OS === 'web' && iframeRef.current?.contentDocument) {
			const doc = iframeRef.current.contentDocument;
			doc.open();
			doc.write(htmlContent);
			doc.close();
		}
	}, [htmlContent]);

	if (Platform.OS !== 'web') {
		return <View style={styles.hiddenContainer} />;
	}

	return (
		<View style={styles.hiddenContainer}>
			{/* @ts-ignore - iframe is web-only */}
			<iframe
				ref={iframeRef}
				style={{
					position: 'absolute',
					width: '1px',
					height: '1px',
					opacity: 0,
					pointerEvents: 'none',
				}}
				title="Action Plans Print"
			/>
		</View>
	);
});

const styles = StyleSheet.create({
	hiddenContainer: {
		position: 'absolute',
		left: -9999,
		top: -9999,
		width: 1,
		height: 1,
		opacity: 0,
	},
});

ActionPlanListPrint.displayName = 'ActionPlanListPrint';
(ActionPlanListPrint as any).PureComponent = true;

export default ActionPlanListPrint;
